import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Calendar } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { useCalendly } from "./CalendlyButton";

const links = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "The Method", to: "/method" },
  { label: "Work With Jenna", to: "/lead" },
  { label: "Resources", to: "/shop" },
];

// Pages whose layout starts flush at y=0 with no full-width dark hero.
// The navbar must render in its solid/opaque state so links stay readable.
const ALWAYS_SOLID_PATHS = ["/free-preview", "/downloads", "/tools", "/payment/success"];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const openCalendly = useCalendly();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  if (location.pathname.startsWith("/admin")) return null;

  const forceSolid = ALWAYS_SOLID_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const solid = scrolled || forceSolid;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,padding] duration-500 ${
          solid ? "backdrop-blur-xl bg-cream/90 border-b border-gold/30 py-4" : "bg-transparent py-6"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="logo-home-link" className="text-left">
            <Wordmark size="sm" />
          </button>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`link-underline font-sans text-sm tracking-wide transition-colors ${
                  location.pathname === l.to ? "text-terracotta" : "text-charcoal hover:text-terracotta"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => openCalendly({ medium: "nav", content: "header-desktop" })}
              data-testid="nav-discovery-cta"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-2.5 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
            >
              <Calendar size={14} />
              Book a Discovery Call
            </button>
          </nav>

          <button
            className="lg:hidden text-wine"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-charcoal text-cream flex flex-col p-8 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center">
              <Wordmark size="sm" className="[&_*]:!text-cream" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-8 mt-24">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-serif text-4xl text-cream hover:text-blush transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + links.length * 0.08 }}
              className="mt-12"
            >
              <button
                onClick={() => {
                  setOpen(false);
                  openCalendly({ medium: "nav", content: "mobile-menu" });
                }}
                data-testid="mobile-nav-discovery-cta"
                className="inline-flex items-center gap-2 rounded-full bg-blush text-wine px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-cream transition-colors duration-300"
              >
                <Calendar size={14} />
                Book a Discovery Call
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
